import { forwardRef, InputHTMLAttributes } from 'react';
import FaIcon from './FaIcon';
import Input from './Input';

/**
 * SearchInput — Input with a leading magnifying-glass icon.
 *
 * Replaces the repeated pattern of:
 *   <div className="relative">
 *     <FaIcon icon="magnifying-glass" className="absolute left-3 top-1/2 -translate-y-1/2 ..." />
 *     <Input className="pl-9" ... />
 *   </div>
 */

interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Additional classes for the outer wrapper */
  wrapperClassName?: string;
  /** Additional classes for the input */
  className?: string;
  /** Whether to take full width (default: true) */
  fullWidth?: boolean;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ wrapperClassName = '', className = '', fullWidth = true, 'aria-label': ariaLabel = 'Search', ...props }, ref) => {
    return (
      <div className={`relative ${fullWidth ? 'w-full' : ''} ${wrapperClassName}`}>
        <FaIcon
          icon="magnifying-glass"
          className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <Input
          ref={ref as React.Ref<HTMLInputElement>}
          type="search"
          aria-label={ariaLabel}
          className={`pl-9 ${className}`}
          fullWidth={fullWidth}
          {...props}
        />
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
